import { SmartusComponentsModule } from '@affinis/smartus-components';
import {
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BaseSelectViewModel,
  BillingAreaClient,
  CreateStrategicBusinessUnitUsageRequest,
  LocationClient,
  UpdateStrategicBusinessUnitUsageRequest,
} from '@core/api';
import { CostcenterSearchFormlyFieldService } from '@core/services/costcenter-search-formly-field-service/costcenter-search-formly-field.service';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OverrideModule } from '@override/override.module';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  catchError,
  filter,
  forkJoin,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { EditAccountingAreasComponent } from '../edit-accounting-areas/edit-accounting-areas.component';

export const DATE_FORMAT = {
  parse: {
    dateInput: 'DD.MM.yyyy',
  },
  display: {
    dateInput: 'DD.MM.yyyy',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-edit-accounting-areas-usage-shares',
  standalone: true,
  imports: [
    SmartusComponentsModule,
    TranslateModule,
    FormlyModule,
    FormsModule,
    ReactiveFormsModule,
    OverrideModule,
    MatTooltipModule,
    MatDialogModule,
  ],
  templateUrl: './edit-accounting-areas-usage-shares.component.html',
  providers: [provideMomentDateAdapter(DATE_FORMAT)],
  styleUrl: './edit-accounting-areas-usage-shares.component.scss',
})
export class EditAccountingAreasUsageSharesComponent
  implements OnInit, OnDestroy
{
  form = new FormGroup({});
  private readonly cd = inject(ChangeDetectorRef);
  private readonly freeWorkspaces$ = new BehaviorSubject<number>(0);

  formFields: Array<FormlyFieldConfig> = [];
  changeReasons: BaseSelectViewModel[] = [];
  strategicBusinessUnitIds: BaseSelectViewModel[] = [];

  formModel = {};

  isLoading = false;

  selectedCostcenter: string;

  minDate = new Date();
  maxDate = new Date('2099-01-01T23:59:00');

  constructor(
    private readonly translateService: TranslateService,
    private readonly locationClient: LocationClient,
    public dialogRef: MatDialogRef<EditAccountingAreasComponent>,
    private readonly costcenterFormlyFieldService: CostcenterSearchFormlyFieldService,
    readonly billingAreaClient: BillingAreaClient,
    readonly toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title?: string;
      id?: string;
      vb_name?: string;
      vbId?: string;
      strategicBusinessUnit?: string;
      costcenter?: string;
      start?: Date;
      end?: Date;
      workspaceCount?: number;
      freeWorkspaceCount?: number;
      isEditMode?: boolean;
      isDispo?: boolean;
    }
  ) {}

  ngOnInit() {
    forkJoin({
      changeReasons: this.locationClient.getChangeReasons().pipe(
        catchError(error => {
          console.error('Error fetching change reasons:', error);

          return of([]);
        })
      ),
      strategicBusinessUnitIds: this.billingAreaClient
        .getStrategicBusinessUnitIds()
        .pipe(
          catchError(error => {
            console.error('Error fetching change reasons:', error);

            return of([]);
          })
        ),
    }).subscribe({
      next: results => {
        this.changeReasons = results.changeReasons;
        this.strategicBusinessUnitIds = results.strategicBusinessUnitIds;

        this.initializeFormFields();
        this.getAvailableWorkspaces().subscribe({
          next: response => {
            this.freeWorkspaces$.next(response);
          },
        });
      },
      error: error => {
        console.error('Error in ngOnInit:', error);
        this.initializeFormFields();
      },
    });

    const today = new Date();
    this.minDate = new Date(today.setDate(today.getDate() + 1));
  }

  ngOnDestroy(): void {
    this.costcenterFormlyFieldService.sbuId = null;
  }

  initializeFormFields() {
    this.costcenterFormlyFieldService.sbuId = this.data.strategicBusinessUnit;
    this.freeWorkspaces$.next(this.data.freeWorkspaceCount);

    this.formFields = [
      {
        type: 'input',
        key: 'vbName',
        wrappers: ['readOnly'],
        defaultValue: this.data?.vb_name || '',
        props: {
          label: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.vb_name'
          ),
        },
      },
      {
        type: 'select',
        key: 'SBU',
        wrappers: this.data.isEditMode ? ['readOnly'] : null,
        defaultValue: this.data?.strategicBusinessUnit || '',
        props: {
          label: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.sbu'
          ),
          options: this.strategicBusinessUnitIds.map(
            (reason: BaseSelectViewModel) => ({
              value: reason.id,
              label: reason.name,
            })
          ),
          required: true,
        },
        hooks: {
          onInit: field => {
            return field.formControl.valueChanges.pipe(
              tap(value => {
                this.costcenterFormlyFieldService.sbuId = value;
                this.costcenterFormlyFieldService.resetSelection();
              }),
              switchMap(() => this.getAvailableWorkspaces()),
              tap(response => {
                this.freeWorkspaces$.next(response);
                field.formControl.updateValueAndValidity({
                  emitEvent: false,
                });
              })
            );
          },
        },
      },
      {
        key: 'costcenter',
        fieldGroup: [
          ...this.costcenterFormlyFieldService.autocompleteSearch(
            this.translateService.instant(
              'accountingArea.accounting_areas.edit_dialog.costcenter'
            ),
            true,
            null,
            this.data?.costcenter || null,
            id => this.onSelectCostcenter(id),
            () => this.onDeleteCostcenter(),
            true
          ),
        ],
      },
      {
        type: 'chapter',
        props: {
          chapterTitle: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.usage_period'
          ),
        },
      },
      {
        type: 'flex-layout-col',
        fieldGroup: [
          {
            type: 'datepicker',
            key: 'usageFrom',
            defaultValue:
              this.data.isDispo === false ? this.data?.start : this.minDate,
            props: {
              label: this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.usage_from'
              ),
              required: true,
              datepickerOptions: {
                min: this.minDate,
              },
            },
            hooks: {
              onInit: field => {
                return field.options.fieldChanges.pipe(
                  filter(e => {
                    return e.type === 'valueChanges' && e.field === field;
                  }),
                  switchMap(() => this.getAvailableWorkspaces()),
                  tap(response => {
                    this.freeWorkspaces$.next(response);
                    field.formControl.updateValueAndValidity({
                      emitEvent: false,
                    });
                  })
                );
              },
            },
            validators: {
              dateValidation: {
                expression: c => {
                  const usageToControl = c.parent?.get('usageTo');

                  if (usageToControl?.value && c.value > usageToControl.value) {
                    return false;
                  }
                  return true;
                },
                message: () =>
                  this.translateService.instant(
                    'accountingArea.accounting_areas.edit_dialog.invalid_start_date'
                  ),
              },
            },
          },
          {
            type: 'datepicker',
            key: 'usageTo',
            defaultValue: this.data?.end
              ? new Date(this.data.end)
              : new Date('2099-01-01'),
            props: {
              label: this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.usage_to'
              ),
              required: true,
              datepickerOptions: {
                max: this.maxDate,
              },
            },
            hooks: {
              onInit: field => {
                return field.options.fieldChanges.pipe(
                  filter(e => {
                    return e.type === 'valueChanges' && e.field === field;
                  }),
                  switchMap(() => this.getAvailableWorkspaces()),
                  tap(response => {
                    this.freeWorkspaces$.next(response);
                    field.formControl.updateValueAndValidity({
                      emitEvent: false,
                    });
                  })
                );
              },
            },
            validators: {
              dateValidation: {
                expression: c => {
                  const usageFromControl = c.parent?.get('usageFrom');

                  if (
                    usageFromControl?.value &&
                    c.value < usageFromControl.value
                  ) {
                    return false;
                  }
                  return true;
                },
                message: () =>
                  this.translateService.instant(
                    'accountingArea.accounting_areas.edit_dialog.invalid_end_date'
                  ),
              },
            },
          },
          {
            type: 'button',
            key: 'today',
            className: 'form-button',
            props: {
              label: this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.today'
              ),
              onClick: () => {
                (this.form.get('usageTo') as AbstractControl).setValue(
                  new Date()
                );
                this.deleteValidationErrorForUsageFrom();
              },
            },
          },
        ],
      },
      {
        type: 'chapter',
        props: {
          chapterTitle: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.number_workplaces_short'
          ),
        },
      },
      {
        type: 'input-with-text',
        key: 'workspaceCount',
        defaultValue: this.data?.workspaceCount,
        props: {
          label: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.number_workplaces_short'
          ),
          placeholder: this.data?.workspaceCount?.toString() || '',
          required: true,
          errorMessage: this.translateService.instant(
            'accountingArea.accounting_areas.column_usages.error_workplace_number'
          ),
        },
        hooks: {
          onInit: field => {
            this.freeWorkspaces$.subscribe(value => {
              field.props.text = `von ${value || 0}`;
              field.props.placeholder = `${value || 0}`;
              field.formControl.updateValueAndValidity({
                emitEvent: false,
              });
            });
          },
        },
        validators: {
          usageAreaValidation: {
            expression: c => {
              const valueStr = c.value?.toString();
              const value = parseInt(valueStr);
              if (
                !this.freeWorkspaces$.value ||
                this.freeWorkspaces$.value === 0
              ) {
                this.setWorkspaceError(
                  this.translateService.instant(
                    'accountingArea.accounting_areas.error.no_workplaces_available'
                  )
                );
                return false;
              } else if (!c.value) {
                this.setWorkspaceError(
                  this.translateService.instant(
                    'accountingArea.accounting_areas.error.workplace_number_required'
                  )
                );
                return false;
              } else if (
                isNaN(value) ||
                valueStr.includes('.') ||
                valueStr.includes(',') ||
                value < 1
              ) {
                this.setWorkspaceError(
                  this.translateService.instant(
                    'accountingArea.accounting_areas.error.workplace_number_not_permitted'
                  )
                );
                return false;
              } else if (value > (this.freeWorkspaces$.value ?? 0)) {
                this.setWorkspaceError(
                  this.translateService.instant(
                    'accountingArea.accounting_areas.error.workplace_number_too_big'
                  )
                );
                return false;
              }
              return true;
            },
          },
        },
      },
      {
        type: 'input-search',
        key: 'changeReason',
        props: {
          label: this.translateService.instant(
            'accountingArea.accounting_areas.edit_dialog.change_reason'
          ),
          options: this.changeReasons.map((reason: BaseSelectViewModel) => ({
            value: reason.id,
            label: reason.name,
          })),
          required: true,
        },
      },
    ];

    this.cd.detectChanges();
  }

  private getAvailableWorkspaces(): Observable<number> {
    const req = {
      billingAreaId: this.data.vbId,
      usageId: this.data.id,
      sbuId: this.form.get('SBU').value,
      from: this.form.get('usageFrom').value,
      until: this.form.get('usageTo').value,
    };

    return this.billingAreaClient.getAvailableWorkspaces(req).pipe(
      catchError(error => {
        console.error('Error fetching available workspaces:', error);

        return of(0);
      })
    );
  }

  setWorkspaceError(errorMessage: string) {
    const workspaceField = this.formFields.find(
      f => f.key === 'workspaceCount'
    );
    if (workspaceField) {
      workspaceField.props.errorMessage = errorMessage;
    }
  }

  onSelectCostcenter(id: string) {
    this.selectedCostcenter = id;
  }

  onDeleteCostcenter() {
    this.selectedCostcenter = null;
  }

  validateForm() {
    this.deleteValidationErrorForUsageFrom();

    if (this.form.valid) {
      this.onSave();
    } else {
      this.form.markAllAsTouched();
    }
  }

  onSave() {
    const formValue = this.form.value as any;
    this.isLoading = true;
    if (this.data.isEditMode) {
      const request: UpdateStrategicBusinessUnitUsageRequest = {
        id: this.data.id,
        billingAreaId: this.data.vbId,
        strategicBusinessUnitId: formValue.costcenter?.searchResult?.sbuId,
        costCenterId: formValue.costcenter?.searchResult?.costCenter,
        usageFrom: formValue.usageFrom,
        usageUntil: formValue.usageTo,
        numberOfWorkspaces: formValue.workspaceCount,
        changeReason: formValue.changeReason,
      };
      this.billingAreaClient
        .updateStrategicBusinessUnitUsage(request)
        .subscribe({
          next: () => {
            this.toastr.success(
              this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.update_success'
              )
            );
            this.isLoading = false;
            this.dialogRef.close(true);
          },
          error: () => {
            this.isLoading = false;
            this.toastr.error(
              this.translateService.instant('generic.errorMessage')
            );
          },
        });
    } else {
      const request: CreateStrategicBusinessUnitUsageRequest = {
        costCenterId: formValue.costcenter?.searchResult?.costCenter,
        createInitialUsage: false,
        billingAreaId: this.data.vbId,
        strategicBusinessUnitId: formValue.SBU || 'GHS',
        usageFrom: formValue.usageFrom,
        usageUntil: formValue.usageTo,
        numberOfWorkspaces: formValue.workspaceCount,
        changeReason: formValue.changeReason,
      };
      this.billingAreaClient
        .createStrategicBusinessUnitUsage(request)
        .subscribe({
          next: () => {
            this.toastr.success(
              this.translateService.instant(
                'accountingArea.accounting_areas.edit_dialog.create_success'
              )
            );
            this.isLoading = false;
            this.dialogRef.close(true);
          },
          error: () => {
            this.isLoading = false;
            this.toastr.error(
              this.translateService.instant('generic.errorMessage')
            );
          },
        });
    }
  }

  deleteValidationErrorForUsageFrom() {
    const control = this.form.get('usageFrom');

    if (control && control.hasError('matDatepickerMin') && !control.dirty) {
      const currentErrors = { ...control.errors };
      delete currentErrors['matDatepickerMin'];

      control.setErrors(
        Object.keys(currentErrors).length > 0 ? currentErrors : null
      );
    }
  }
}
